import { Link } from 'react-router-dom'
import { Puzzle, Tag } from 'lucide-react'
import type { Skill } from '@/types'
import { SkillActionsMenu } from './SkillActionsMenu'
import { SkillCategoryBadge, SkillVersionBadge } from './SkillBadges'

interface SkillCardProps {
  projectId: string
  skill: Skill
}

export function SkillCard({ projectId, skill }: SkillCardProps) {
  const titleId = `skill-card-title-${skill.id}`
  const detailPath = `/projects/${projectId}/workflows/skills/${skill.id}`

  return (
    <article
      aria-labelledby={titleId}
      className="relative flex h-full flex-col gap-3 rounded-2xl border border-border bg-card p-5 transition-shadow hover:shadow-md"
      data-testid={`skill-card-${skill.name}`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex min-w-0 items-center gap-2">
          <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground">
            <Puzzle className="h-4 w-4" />
          </span>
          <h3 id={titleId} className="min-w-0 truncate text-sm font-bold text-foreground">
            <Link
              to={detailPath}
              className="truncate rounded-sm after:absolute after:inset-0 after:content-[''] hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              {skill.displayName}
            </Link>
          </h3>
        </div>
        <div className="relative z-10">
          <SkillActionsMenu projectId={projectId} skill={skill} />
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-1.5">
        <SkillCategoryBadge skill={skill} />
        {skill.latestVersion && <SkillVersionBadge version={skill.latestVersion} />}
      </div>

      {skill.description && <p className="line-clamp-2 text-sm text-muted-foreground">{skill.description}</p>}

      <span className="mt-auto flex items-center gap-1.5 pt-1 font-mono text-xs text-muted-foreground">
        <Tag className="h-3.5 w-3.5 shrink-0" />
        <span className="truncate">{skill.name}</span>
      </span>
    </article>
  )
}
